/**
 * Security & Privacy Settings Page Component
 * 
 * Main page for managing user security and privacy settings
 * Includes password management, 2FA, biometric authentication, privacy settings,
 * active sessions, and account actions
 * 
 * Features:
 * - Explicit save with button state changes (grey → blue)
 * - Navigation guard for unsaved changes
 * - Four main sections: Authentication, Privacy Settings, Active Sessions, Account Actions
 * - WCAG 2.2 AA compliant
 * 
 * @see specs/007-security-privacy-settings/spec.md
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  Shield,
  Lock,
  Fingerprint,
  Clock,
  Eye,
  Users,
  Download,
  Trash2,
  ChevronDown,
  Settings,
  BarChart3,
  Mail,
  Monitor
} from 'lucide-react';
import { useSecuritySettings, useUpdateSecuritySettings } from '../../hooks/useSecuritySettings';
import { usePrivacySettings, useUpdatePrivacySettings } from '../../hooks/usePrivacySettings';
import { setNavigationGuardState } from './useNavigationGuardState';
import { securityService } from '../../services/security-service';
import { PageHeader } from '../../components/PageHeader';
import { profileService } from '../../services/profile-service';
import { supabaseAuthService } from '../../services/supabase-service';
import { getCurrentUserId } from '../../lib/supabase';
import { useDemoMode } from '../../hooks/useDemoMode';
import { ChangePasswordModal } from './components/ChangePasswordModal';
import { ExportDataModal } from './components/ExportDataModal';
import { DeleteAccountModal } from './components/DeleteAccountModal';
import { TwoFactorSetupModal } from './components/TwoFactorSetupModal';
import { BackupCodesModal } from './components/BackupCodesModal';
import { SessionsListModal } from './components/SessionsListModal';
import type {
  UpdateSecuritySettingsInput,
  UpdatePrivacySettingsInput,
} from '../../types/security.types';
import type {
  ChangePasswordInput,
  DeleteAccountInput,
  ExportDataOptions,
} from '../../types/profile.types';

export function SecurityPrivacyPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is in demo mode
  const { isDemoMode } = useDemoMode();
  
  // Data fetching
  const { data: securitySettings, isLoading: isLoadingSecurity, error: securityError } = useSecuritySettings();
  const { data: privacySettings, isLoading: isLoadingPrivacy, error: privacyError } = usePrivacySettings();
  const updateSecurityMutation = useUpdateSecuritySettings();
  const updatePrivacyMutation = useUpdatePrivacySettings();

  // Form state
  const [securityFormData, setSecurityFormData] = useState<UpdateSecuritySettingsInput>({});
  const [privacyFormData, setPrivacyFormData] = useState<UpdatePrivacySettingsInput>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Modal states
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showTwoFactorSetupModal, setShowTwoFactorSetupModal] = useState(false);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [backupCodesMode, setBackupCodesMode] = useState<'view' | 'regenerate'>('view');
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showExportDataModal, setShowExportDataModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  // Session timeout is handled by SessionTimeoutProvider in App.tsx

  // Initialize form data when settings load
  useEffect(() => {
    if (securitySettings) {
      setSecurityFormData({
        biometricEnabled: securitySettings.biometricEnabled,
        sessionTimeoutMinutes: securitySettings.sessionTimeoutMinutes,
      });
    }
  }, [securitySettings]);

  useEffect(() => {
    if (privacySettings) {
      setPrivacyFormData({
        privacyModeEnabled: privacySettings.privacyModeEnabled,
        dataAnalyticsEnabled: privacySettings.dataAnalyticsEnabled,
        marketingCommunicationsEnabled: privacySettings.marketingCommunicationsEnabled,
      });
    }
  }, [privacySettings]);

  // Track dirty state
  const dirtyState = useMemo(() => {
    if (!securitySettings || !privacySettings) return false;

    return (
      securityFormData.biometricEnabled !== securitySettings.biometricEnabled ||
      securityFormData.sessionTimeoutMinutes !== securitySettings.sessionTimeoutMinutes ||
      privacyFormData.privacyModeEnabled !== privacySettings.privacyModeEnabled ||
      privacyFormData.dataAnalyticsEnabled !== privacySettings.dataAnalyticsEnabled ||
      privacyFormData.marketingCommunicationsEnabled !== privacySettings.marketingCommunicationsEnabled
    );
  }, [securityFormData, privacyFormData, securitySettings, privacySettings]);

  // Update dirty state when computed value changes
  useEffect(() => {
    setIsDirty(dirtyState);
    setHasUnsavedChanges(dirtyState);
  }, [dirtyState]);

  // Navigation guard for unsaved changes
  const previousLocationRef = useRef(location.pathname);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }

    if (previousLocationRef.current !== location.pathname && hasUnsavedChanges) {
      window.history.pushState(null, '', previousLocationRef.current);
      setPendingNavigation(location.pathname);
      setShowNavigationDialog(true);
    }

    previousLocationRef.current = location.pathname;
  }, [location.pathname, hasUnsavedChanges]);

    useEffect(() => {
      const handlePopState = () => {
        if (hasUnsavedChanges) {
          window.history.pushState(null, '', location.pathname);
          setPendingNavigation(previousLocationRef.current);
          setShowNavigationDialog(true);
        }
      };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges, location.pathname]);

  // Register guard state with SettingsLayout
  useEffect(() => {
    setNavigationGuardState(hasUnsavedChanges, handleNavigationAttempt);
    return () => {
      setNavigationGuardState(false, null);
    };
  }, [hasUnsavedChanges]);

  const handleNavigationAttempt = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowNavigationDialog(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  // Save changes handler
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;

    setSaveStatus('saving');
    setErrorMessage('');

    try {
      // Update security settings if changed
      if (securityFormData.biometricEnabled !== undefined || securityFormData.sessionTimeoutMinutes !== undefined) {
        await updateSecurityMutation.mutateAsync(securityFormData);
      }

      // Update privacy settings if changed
      if (Object.keys(privacyFormData).length > 0) {
        await updatePrivacyMutation.mutateAsync(privacyFormData);
      }

      setSaveStatus('saved');
      setIsDirty(false);
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
      return true;
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to save settings');
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
        setErrorMessage('');
      }, 5000);
      return false;
    }
  }, [isDirty, securityFormData, privacyFormData, updateSecurityMutation, updatePrivacyMutation]);

  // Navigation dialog handlers
  const handleNavigationDialogSave = useCallback(async () => {
    if (pendingNavigation) {
      const saveSuccessful = await handleSave();
      if (saveSuccessful) {
        setShowNavigationDialog(false);
        setPendingNavigation(null);
      }
    }
  }, [pendingNavigation, handleSave]);

  const handleNavigationDialogDiscard = useCallback(() => {
    setShowNavigationDialog(false);
    if (securitySettings && privacySettings) {
      setSecurityFormData({
        biometricEnabled: securitySettings.biometricEnabled,
        sessionTimeoutMinutes: securitySettings.sessionTimeoutMinutes,
      });
      setPrivacyFormData({
        privacyModeEnabled: privacySettings.privacyModeEnabled,
        dataAnalyticsEnabled: privacySettings.dataAnalyticsEnabled,
        marketingCommunicationsEnabled: privacySettings.marketingCommunicationsEnabled,
      });
      setIsDirty(false);
      setHasUnsavedChanges(false);
    }
    if (pendingNavigation) {
      isNavigatingRef.current = true;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate, securitySettings, privacySettings]);

  const handleNavigationDialogCancel = useCallback(() => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  }, []);

  // Field change handlers
  const handleSecurityFieldChange = (field: keyof UpdateSecuritySettingsInput) => (
    value: any
  ) => {
    setSecurityFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrivacyFieldChange = (field: keyof UpdatePrivacySettingsInput) => (
    value: boolean
  ) => {
    setPrivacyFormData(prev => ({ ...prev, [field]: value }));
  };

  // Password change handler
  const handlePasswordChange = async (input: ChangePasswordInput) => {
    try {
      const result = await supabaseAuthService.changePassword(input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      // Update password changed timestamp
      const userId = await getCurrentUserId();
      if (userId) {
        await securityService.updateSecuritySettings(userId, {
          passwordChangedAt: new Date(),
        });
      }
      setShowChangePasswordModal(false);
      // Refresh security settings
      window.location.reload(); // Simple refresh to update password changed timestamp
    } catch (error: any) {
      throw error;
    }
  };

  // 2FA handlers
  const handle2FASuccess = async () => {
    // Refresh security settings to show updated 2FA status
    window.location.reload();
  };

  const handleDisable2FA = async () => {
    const userId = await getCurrentUserId();
    if (!userId) return;

    const password = prompt('Enter your password to disable 2FA:');
    if (!password) return;

    try {
      const result = await securityService.disable2FA(userId, { password });
      if (result.error) {
        alert(result.error.message);
        return;
      }
      window.location.reload();
    } catch (error: any) {
      alert(error.message || 'Failed to disable 2FA');
    }
  };

  // Export data handler
  const handleExportData = async (options: ExportDataOptions) => {
    try {
      const result = await profileService.exportData(options);
      if (result.error) {
        throw new Error(result.error.message);
      }
      setShowExportDataModal(false);
    } catch (error: any) {
      throw error;
    }
  };

  // Delete account handler
  const handleDeleteAccount = async (input: DeleteAccountInput) => {
    try {
      const result = await profileService.deleteAccount(input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      // deleteAccount already signs out; send user to login
      navigate('/login', { replace: true });
    } catch (error: any) {
      throw error;
    }
  };

  // Session timeout is handled globally by SessionTimeoutProvider
  // No local session timeout logic needed here

  if (isLoadingSecurity || isLoadingPrivacy) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (securityError || privacyError || !securitySettings || !privacySettings) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Security & Privacy"
          description="Manage your account security and privacy settings"
          helpSlug="settings-security"
          headingLevel="h2"
        />
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <p>{securityError?.message || privacyError?.message || 'Failed to load settings'}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatPasswordChangedDate = (date: Date | null): string => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Security & Privacy"
        description="Manage your account security and privacy settings"
        helpSlug="settings-security"
        headingLevel="h2"
      />

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="h-5 w-5" />
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {saveStatus === 'saved' && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <CheckCircle className="h-5 w-5" />
            <p>Settings saved successfully</p>
          </div>
        </div>
      )}

      {/* Authentication Management Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Authentication Management
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage your password, two-factor authentication, and biometric login
          </p>
        </div>

        <div className="space-y-6">
          {/* Password Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    Password
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Last changed: {formatPasswordChangedDate(securitySettings.passwordChangedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(true)}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
              >
                Change Password
              </button>
            </div>
          </div>

          {/* Two-Factor Authentication Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    Two-Factor Authentication
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Add an extra layer of security to your account
                </p>
              </div>
              <div className="flex items-center gap-2">
                {securitySettings.twoFactorEnabled ? (
                  <>
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium">Enabled</span>
                    <button
                      type="button"
                      onClick={() => setShowBackupCodesModal(true)}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
                    >
                      View Backup Codes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBackupCodesMode('regenerate');
                        setShowBackupCodesModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
                    >
                      Regenerate Codes
                    </button>
                    <button
                      type="button"
                      onClick={handleDisable2FA}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
                    >
                      Disable 2FA
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowTwoFactorSetupModal(true)}
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
                  >
                    Enable 2FA
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Biometric Authentication Section */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Fingerprint className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                    Biometric Authentication
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Use Face ID, Touch ID, or Windows Hello for faster login
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleSecurityFieldChange('biometricEnabled')(
                  !(securityFormData.biometricEnabled ?? securitySettings.biometricEnabled)
                )}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  (securityFormData.biometricEnabled ?? securitySettings.biometricEnabled)
                    ? 'bg-primary-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={securityFormData.biometricEnabled ?? securitySettings.biometricEnabled}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    (securityFormData.biometricEnabled ?? securitySettings.biometricEnabled)
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Session Timeout Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
                Session Timeout
              </h4>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Automatically sign out after inactivity
              </p>
              <div className="relative">
                <select
                  value={securityFormData.sessionTimeoutMinutes ?? securitySettings.sessionTimeoutMinutes}
                  onChange={(e) => handleSecurityFieldChange('sessionTimeoutMinutes')(parseInt(e.target.value, 10))}
                  className="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2 pr-8 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              You'll receive a warning 2 minutes before your session expires
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Settings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Privacy Settings
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Control how your data is used and displayed
          </p>
        </div>

        <div className="space-y-4">
          {/* Privacy Mode Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Eye className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label className="font-medium text-gray-900 dark:text-gray-100">
                  Privacy Mode
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Hide sensitive data in screenshots
              </p>
            </div>
            <button
              type="button"
              onClick={() => handlePrivacyFieldChange('privacyModeEnabled')(
                !(privacyFormData.privacyModeEnabled ?? privacySettings.privacyModeEnabled)
              )}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                (privacyFormData.privacyModeEnabled ?? privacySettings.privacyModeEnabled)
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={privacyFormData.privacyModeEnabled ?? privacySettings.privacyModeEnabled}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  (privacyFormData.privacyModeEnabled ?? privacySettings.privacyModeEnabled)
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Data Analytics Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label className="font-medium text-gray-900 dark:text-gray-100">
                  Data Analytics
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Help improve the app with anonymous usage data
              </p>
            </div>
            <button
              type="button"
              onClick={() => handlePrivacyFieldChange('dataAnalyticsEnabled')(
                !(privacyFormData.dataAnalyticsEnabled ?? privacySettings.dataAnalyticsEnabled)
              )}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                (privacyFormData.dataAnalyticsEnabled ?? privacySettings.dataAnalyticsEnabled)
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={privacyFormData.dataAnalyticsEnabled ?? privacySettings.dataAnalyticsEnabled}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  (privacyFormData.dataAnalyticsEnabled ?? privacySettings.dataAnalyticsEnabled)
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Marketing Communications Toggle */}
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <label className="font-medium text-gray-900 dark:text-gray-100">
                  Marketing Communications
                </label>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Receive product updates and financial tips
              </p>
            </div>
            <button
              type="button"
              onClick={() => handlePrivacyFieldChange('marketingCommunicationsEnabled')(
                !(privacyFormData.marketingCommunicationsEnabled ?? privacySettings.marketingCommunicationsEnabled)
              )}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                (privacyFormData.marketingCommunicationsEnabled ?? privacySettings.marketingCommunicationsEnabled)
                  ? 'bg-primary-600'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={privacyFormData.marketingCommunicationsEnabled ?? privacySettings.marketingCommunicationsEnabled}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  (privacyFormData.marketingCommunicationsEnabled ?? privacySettings.marketingCommunicationsEnabled)
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Active Sessions Section */}
      {!isDemoMode && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Active Sessions
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              View and manage your active login sessions
            </p>
          </div>
          <div className="flex items-center justify-between py-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Monitor className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div className="font-medium text-gray-900 dark:text-gray-100">Login Sessions</div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage active sessions across devices
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSessionsModal(true)}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
            >
              View Sessions
            </button>
          </div>
        </div>
      )}

      {/* Account Actions Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Account Actions
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Export your data or delete your account
          </p>
        </div>

        <div className="space-y-0">
          {/* Export All Data */}
          <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Download className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div className="font-medium text-gray-900 dark:text-gray-100">Export All Data</div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Download your data in JSON or CSV format
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowExportDataModal(true)}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium text-sm"
            >
              Export Data
            </button>
          </div>

          {/* Delete Account */}
          <div className="flex items-center justify-between py-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Trash2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div className="font-medium text-gray-900 dark:text-gray-100">Delete Account</div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Permanently delete your account and all data (30-day recovery window)
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowDeleteAccountModal(true)}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium text-sm"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Save Changes Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saveStatus === 'saving'}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-colors ${
            isDirty
              ? 'bg-primary-600 hover:bg-primary-700 text-white'
              : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
          aria-disabled={!isDirty}
        >
          {saveStatus === 'saving' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : saveStatus === 'saved' ? (
            <>
              <CheckCircle className="h-4 w-4" />
              <span>Saved</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      {/* Navigation Guard Dialog */}
      {showNavigationDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="navigation-dialog-title"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 id="navigation-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Unsaved Changes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                You have unsaved changes. What would you like to do?
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  type="button"
                  onClick={handleNavigationDialogCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogDiscard}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Discard Changes
                </button>
                <button
                  type="button"
                  onClick={handleNavigationDialogSave}
                  disabled={saveStatus === 'saving'}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saveStatus === 'saving' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        onSubmit={handlePasswordChange}
        loading={false}
      />
      <TwoFactorSetupModal
        isOpen={showTwoFactorSetupModal}
        onClose={() => setShowTwoFactorSetupModal(false)}
        onSuccess={handle2FASuccess}
      />
      <BackupCodesModal
        isOpen={showBackupCodesModal}
        onClose={() => {
          setShowBackupCodesModal(false);
          setBackupCodesMode('view');
        }}
        mode={backupCodesMode}
      />
      <SessionsListModal
        isOpen={showSessionsModal}
        onClose={() => setShowSessionsModal(false)}
        onSessionRevoked={() => {
          // Refresh sessions if needed
        }}
      />
      <ExportDataModal
        isOpen={showExportDataModal}
        onClose={() => setShowExportDataModal(false)}
        onSubmit={handleExportData}
        loading={false}
      />
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onSubmit={handleDeleteAccount}
        loading={false}
      />
    </div>
  );
}

